/* eslint-disable react/prop-types */
import React, { useEffect, useState, useRef, useCallback } from 'react';
import { connect } from 'react-redux';

import {
    selectSection,
    editProduct,
    removeProduct,
    selectNamedProduct,
    setTorsoOverlay,
    removeBuild,
    toggleNavigation,
} from '../../actions';

import SelectedProduct from '../../entities/SelectedProduct';
import SkinToneFilters from '../Navigation/SkinToneFilters';
import PreviewItem from './PreviewItem';
import Warning from './Warning';

const Preview = ({
    selected,
    dispatch,
    projectTitle,
    activeSection,
    skintone,
    torsoOverlays,
    buying,
    currentBuildIndex,
}) => {
    const [showSkinToneSelector, setShowSkinToneSelector] = useState(false);
    const [showWarning, setShowWarning] = useState(false);
    const [showResetWarning, setShowResetWarning] = useState(false);
    const [showBuyWarning, setShowBuyWarning] = useState(false);
    const previewItems = [
        {
            id: 'hatsAndHair',
            title: 'Hats & Hair',
            emptyText: 'Choose a hat or some hair',
            defaultImage: 'https://cdn.shopify.com/s/files/1/0223/5446/0736/files/default-hair.svg?v=1661858104',
            handle: 'hair-and-hats',
        },
        {
            id: 'heads',
            title: 'Heads',
            emptyText: 'Choose a head',
            defaultImage: 'https://cdn.shopify.com/s/files/1/0223/5446/0736/files/default-head.svg?v=1661858105',
            handle: 'all-heads-new-1',
        },
        {
            id: 'bodies',
            title: 'Bodies',
            emptyText: 'Choose a body',
            defaultImage: 'https://cdn.shopify.com/s/files/1/0223/5446/0736/files/default-torso.svg?v=1661858104',
            handle: 'all-torsos-new-website',
        },
        {
            id: 'legs',
            title: 'Legs',
            emptyText: 'Choose a pair of legs',
            defaultImage: 'https://cdn.shopify.com/s/files/1/0223/5446/0736/files/default-legs.svg?v=1661858104',
            handle: 'all-legs',
        },
        {
            id: 'display',
            title: 'Display',
            emptyText: 'Add a display',
            defaultImage: 'https://cdn.shopify.com/s/files/1/0223/5446/0736/files/default-display.svg?v=1661858104',
            handle: 'all-displays',
        },
    ];
    const formRef = useRef(null);
    const [headVariantId, setHeadVariantId] = useState([null]);

    const updateTorsoOverlays = useCallback(() => {
        const selectedBody = selected?.bodies;
        const handImages = {
            dark: 'https://cdn.shopify.com/s/files/1/0223/5446/0736/files/Dark-Flesh.png?v=1665587356',
            light: 'https://cdn.shopify.com/s/files/1/0223/5446/0736/files/Light-Flesh.png?v=1665587356',
            yellow: 'https://cdn.shopify.com/s/files/1/0223/5446/0736/files/Yellow.png?v=1665587356',
            medium: 'https://cdn.shopify.com/s/files/1/0223/5446/0736/files/Medium-Flesh.png?v=1665587363',
            nougat: 'https://cdn.shopify.com/s/files/1/0223/5446/0736/files/Medium-Flesh.png?v=1665587363',
        };
        if (!selectedBody) return;

        let activeTorso = selectedBody?.attributes;
        if (selectedBody.product.variants.nodes.length === 1) {
            activeTorso = selectedBody.product.variants.nodes[0];
        }

        const imageStringToFind = `${
            activeTorso.torsoOverlayValue?.value === 'Light' ? 'Colour' : activeTorso.torsoOverlayValue?.value
        }-Artwork---${skintone.toLowerCase()}`;
        const defaultImageStrings = `${
            activeTorso.torsoOverlayValue?.value === 'Light' ? 'Colour' : activeTorso.torsoOverlayValue?.value
        }-Artwork---default`;
        let torsoOverlayImage = '';
        selectedBody.product.images.edges.forEach((image) => {
            if (image.node.url.toLowerCase().includes(imageStringToFind.toLowerCase())) {
                torsoOverlayImage = image.node.url;
            } else if (image.node.url.toLowerCase().includes(defaultImageStrings.toLowerCase())) {
                torsoOverlayImage = image.node.url;
            }
        });
        const handImage = handImages[skintone.toLowerCase()];
        const torsoOverlayImages = {
            handImage,
            torsoOverlayImage,
        };

        dispatch(setTorsoOverlay(torsoOverlayImages));
    }, [dispatch, selected?.bodies, skintone]);

    useEffect(() => {
        const value = selected?.heads?.productMatchAttr?.skintoneValue?.value.toLowerCase()
            ? selected?.heads?.productMatchAttr?.skintoneValue?.value.toLowerCase()
            : selected?.heads?.attributes?.skintoneValue?.value.toLowerCase();

        if (value && value.toLowerCase() !== skintone.toLowerCase()) {
            const headProduct = selected?.heads?.product;
            const headVariants = headProduct?.variants?.edges
                ? headProduct?.variants?.edges
                : headProduct?.variants?.nodes;
            const headVariant = headVariants?.filter((head) => {
                if (head.node) {
                    return head.node.skintoneValue.value.toLowerCase() === skintone.toLowerCase();
                } else {
                    return head.skintoneValue.value.toLowerCase() === skintone.toLowerCase();
                }
            });

            if (headVariant?.length > 0) {
                if (headVariant[0]?.node) {
                    setHeadVariantId(headVariant[0]?.node?.id?.split('/').pop());
                } else {
                    setHeadVariantId(headVariant[0]?.id?.split('/').pop());
                }
            } else {
                setHeadVariantId([]);
            }
        } else {
            setShowWarning(false);
        }
    }, [selected, skintone]);

    const updateHeadSkintone = useCallback(
        (headVariant) => {
            const selectedHead = selected?.heads;
            if (!selectedHead) return;
            const headVar = headVariant[0]?.node ? headVariant[0]?.node : headVariant[0];
            if (headVar) {
                if (!selectedHead.productMatchAttr || headVar.id !== selectedHead?.productMatchAttr?.id) {
                    dispatch(
                        selectNamedProduct(
                            currentBuildIndex,
                            selectedHead.product,
                            null,
                            null,
                            headVar,
                            selectedHead.category,
                            selectedHead.productFormJsonValues
                        )
                    );
                }
            } else {
                setShowWarning(true);
            }
        },
        [dispatch, selected?.heads, currentBuildIndex]
    );

    useEffect(() => {
        if (updateTorsoOverlays) {
            updateTorsoOverlays();
        }
    }, [skintone, updateTorsoOverlays]);

    useEffect(() => {
        const headProduct = selected?.heads?.product;
        const headVariants = headProduct?.variants?.edges ? headProduct?.variants?.edges : headProduct?.variants?.nodes;
        const headVariant = headVariants?.filter((head) => {
            if (head.node) {
                return head.node.skintoneValue.value.toLowerCase() === skintone.toLowerCase();
            } else {
                return head.skintoneValue.value.toLowerCase() === skintone.toLowerCase();
            }
        });

        updateHeadSkintone(headVariant);
    }, [headVariantId, selected?.heads?.product, skintone, updateHeadSkintone]);

    const accessoryInfo = {
        title: 'Accessory',
        emptyText: 'Add an accessory',
        defaultImage: '',
        handle: 'all-accessories',
    };

    const accessories = ['accessory1', 'accessory2', 'accessory3', 'accessory4'];

    const onEdit = (sectionId, product) => {
        Promise.resolve(
            dispatch(selectSection(sectionId)).then(function (response) {
                dispatch(editProduct(sectionId, product));
                return response;
            })
        );
    };

    const onRemove = (sectionId) => dispatch(removeProduct(currentBuildIndex, sectionId));

    return (
        <section className={`${buying ? 'buying' : ''} preview-section`}>
            <article className="preview-container">
                <h2 className="preview-projectTitle">
                    Your project
                    <span>{projectTitle?.split(/[-*]/)[0]}</span>
                </h2>
                <button
                    onClick={() => {
                        console.log('clicked');
                        dispatch(toggleNavigation());
                    }}
                    className="editProjectTitle"
                >
                    Edit project title
                </button>

                <div className="skintone-selector">
                    {showSkinToneSelector ? (
                        <SkinToneFilters
                            projectTitle={projectTitle}
                            activeSection={activeSection}
                            setShowSkinToneSelector={setShowSkinToneSelector}
                            titleText="Change skin tone"
                        />
                    ) : (
                        <div className="skintone-selector__toggle">
                            <span className={skintone?.toLowerCase()} />
                            <button onClick={() => setShowSkinToneSelector(true)}>Change skin tone</button>
                        </div>
                    )}
                </div>

                <div className="preview-items">
                    {previewItems.map((item) => (
                        <PreviewItem
                            key={item.id}
                            item={selected[item.id]}
                            info={item}
                            dispatch={dispatch}
                            onEdit={onEdit}
                            onRemove={onRemove}
                            skintone={skintone}
                            torsoOverlays={torsoOverlays}
                        />
                    ))}
                </div>

                <div style={{ display: 'none' }}>
                    <h2>Head replacement form</h2>
                    <form
                        action="/cart/add"
                        method="post"
                        encType="multipart/form-data"
                        id={`AddToCartForm-${headVariantId}`}
                        ref={formRef}
                    >
                        <input type="number" name="quantity" value="1" hidden readOnly />
                        <input name="id" value={headVariantId} hidden readOnly />
                        <input
                            type="text"
                            id={`projectTitle-${headVariantId}`}
                            name="properties[projectTitle]"
                            value={projectTitle ? projectTitle : ''}
                            hidden
                            readOnly
                        />
                    </form>
                </div>

                {showWarning && (
                    <Warning
                        actionConfirm={() => {
                            setShowWarning(false);
                            dispatch(removeProduct(currentBuildIndex, 'heads'));
                        }}
                        warningText="The selected head is not available in the chosen skin tone, and will now be removed"
                    />
                )}

                {showBuyWarning && (
                    <Warning
                        actionConfirm={() => {
                            setShowBuyWarning(false);
                        }}
                        warningText="Please select a head, body, and some legs to continue."
                    />
                )}

                <h2>Extras</h2>
                <div className="preview-accessories">
                    {accessories.map((accessory) => (
                        <PreviewItem
                            key={accessory}
                            item={selected[accessory]}
                            info={{
                                ...accessoryInfo,
                                id: accessory,
                            }}
                            dispatch={dispatch}
                            onEdit={onEdit}
                            onRemove={onRemove}
                            skintone={skintone}
                            isAccessoryItem
                        />
                    ))}
                </div>

                <div className="preview-actions">
                    <span className="preview-total">
                        Total: <b>£{SelectedProduct?.calculateCost(selected)?.toFixed(2)}</b>
                    </span>
                </div>
            </article>

            {/* <div className="preview-buttons row">
                <button
                    type="button"
                    className="button btn span preview-buy"
                    onClick={(e) => onBuy()}
                    disabled={!hasSelectedProduct(selected)}
                >
                    <span>Buy now</span>
                </button>
            </div> */}
            {/* <button className="reset-button" onClick={onReset}>
                Delete this project
            </button> */}

            {showResetWarning && (
                <div className="reset-overlay">
                    <Warning
                        actionConfirm={() => {
                            dispatch(removeBuild(currentBuildIndex));
                            setShowResetWarning(false);

                            window.location.href = '/pages/minifig-builder';
                        }}
                        actionDeny={() => {
                            setShowResetWarning(false);
                        }}
                        warningText="Are you sure you want to delete this project?"
                    />
                </div>
            )}
        </section>
    );
};

const mapStateToProps = (state) => {
    return {
        currentBuildIndex: state.currentBuildIndex,
        projectTitle: state.builds[state.currentBuildIndex].projectTitle,
        activeSection: state.activeSection,
        skintone: state.builds[state.currentBuildIndex].skintone,
        torsoOverlays: state.builds[state.currentBuildIndex].torsoOverlays,
        buying: state.buying,
    };
};

export default connect(mapStateToProps)(Preview);
